<template>
  <div class="c-calendar">
    <div v-if="value.length" class="c-calendar-select">
      <p v-if="selected.label" class="c-calendar-select__label">
        {{ selected.label }}
      </p>
      <Button
        size="w240"
        fsize="f14"
        variant="border"
        icon="date"
        radius
        @click="click"
      >
        {{ value[0] | dateText(dateFormat) }}-{{
          value[1] | dateText(dateFormat)
        }}
      </Button>
    </div>
    <Modal v-if="isVisible" variant="calendar" @close="close">
      <template slot="content">
        <div class="c-calendar__content">
          <div class="c-calendar__datepicker">
            <v-date-picker
              v-model="temp.value"
              :columns="calendar.columns"
              :masks="calendar.masks"
              :max-date="calendar.maxDate"
              :step="calendar.step"
              :to-page="calendar.toPage"
              :transition="calendar.transition"
              is-range
              @dayclick="dayclick"
              @drag="drag"
              @input="input"
            />
          </div>
          <div class="c-calendar__side">
            <ul class="c-calendar-list">
              <li
                v-for="(item, index) in list[listType]"
                :key="index"
                class="c-calendar-list__item"
              >
                <Button
                  block
                  :class="{
                    'is-active': item.label === temp.selected.label
                  }"
                  @click="selectListItem(item)"
                >
                  {{ item.label }}
                </Button>
              </li>
              <li v-if="hasYoY" class="c-calendar-list__item">
                <Button
                  block
                  :class="{
                    'is-active': list.yoy.label === temp.selected.label
                  }"
                  @click="selectListItem(list.yoy)"
                >
                  {{ list.yoy.label }}
                </Button>
              </li>
            </ul>
            <div class="c-calendar__btns">
              <Button
                block
                size="w145"
                fsize="f14"
                variant="main"
                @click="submit"
              >
                適用する
              </Button>
              <Button
                block
                size="w145"
                fsize="f14"
                variant="cancel"
                @click="cancel"
              >
                キャンセル
              </Button>
            </div>
          </div>
        </div>
      </template>
    </Modal>
  </div>
</template>

<script>
import Button from '@/components/Button';
import Modal from '@/components/Modal';
import utils from '@/utils';

export default {
  props: {
    range: {
      type: Array,
      required: true
    },
    listType: {
      type: String,
      required: false,
      default: 'ga'
    },
    value: {
      type: Array,
      required: true
    },
    hasYoY: {
      type: Boolean,
      required: false
    }
  },

  components: {
    Button,
    Modal
  },

  data() {
    return {
      calendar: {
        columns: 3,
        maxDate: '',
        masks: {
          title: 'YYYY年 M月'
        },
        step: 1,
        toPage: {
          month: '',
          year: ''
        },
        transition: 'slide'
      },
      list: {
        ga: [
          {
            display: 'daily',
            label: '過去7日間',
            unit: 'day',
            value: 7
          },
          {
            display: 'daily',
            label: '過去30日間',
            unit: 'day',
            value: 30
          },
          {
            display: 'daily',
            label: '過去3ヶ月間',
            unit: 'month',
            value: 3
          },
          {
            display: 'daily',
            label: '過去6ヶ月間',
            unit: 'month',
            value: 6
          },
          {
            display: 'daily',
            label: '過去1年間',
            unit: 'year',
            value: 1
          },
          {
            display: 'daily',
            label: '過去16ヶ月間',
            unit: 'month',
            value: 16
          }
        ],
        gsc: [
          {
            display: 'daily',
            label: '過去7日間',
            unit: 'day',
            value: 7
          },
          {
            display: 'daily',
            label: '過去28日間',
            unit: 'day',
            value: 28
          },
          {
            display: 'daily',
            label: '過去3ヶ月間',
            unit: 'month',
            value: 3
          },
          {
            display: 'daily',
            label: '過去6ヶ月間',
            unit: 'month',
            value: 6
          },
          {
            display: 'daily',
            label: '過去12ヶ月間',
            unit: 'month',
            value: 12
          },
          {
            display: 'daily',
            label: '過去16ヶ月間',
            unit: 'month',
            value: 16
          }
        ],
        yoy: {
          display: 'monthly',
          label: '前年対比',
          type: 'comparison',
          unit: 'year',
          value: 2
        }
      },
      selected: {},
      temp: {
        dragStartDate: '',
        selected: {},
        value: {
          start: '',
          end: ''
        }
      },
      isVisible: false
    };
  },

  computed: {
    dateFormat() {
      if (this.selected.display === 'monthly') {
        return 'YYYY.MM';
      } else {
        return 'YYYY.MM.DD';
      }
    }
  },

  watch: {
    listType: {
      handler() {
        this.init();
      },
      immediate: true
    },
    'temp.selected': {
      handler(value) {
        // 前年対比を選んだ場合は、カレンダーのmaxDateを今月末に設定する
        let maxDate = new Date(this.range[1]);
        if (value.type === 'comparison' && value.unit === 'year') {
          maxDate = utils.getEndDate(maxDate, 'month');
        }
        this.calendar.maxDate = maxDate;
      }
    },
    isVisible: {
      handler(value) {
        if (value) {
          this.adjustPage();
        }
      },
      immediate: true
    }
  },

  methods: {
    adjustPage() {
      let startYear = this.temp.value.start.getFullYear();
      let endYear = this.temp.value.end.getFullYear();
      let startMonth = this.temp.value.start.getMonth() + 1;
      let endMonth = this.temp.value.end.getMonth() + 1;
      let toPage = '';

      // 開始日と終了日が同じ年&&同じ月の場合は、その月がカレンダーの中央に表示されるようにする
      if (startYear === endYear && startMonth === endMonth) {
        toPage = new Date(this.temp.value.start);
        toPage.setMonth(toPage.getMonth() + 1);
      } else {
        toPage = this.temp.value.end;
      }

      this.calendar.toPage = Object.assign(
        {},
        {
          month: toPage.getMonth() + 1,
          year: toPage.getFullYear()
        }
      );
    },

    cancel() {
      this.updateTempValue(this.value[0], this.value[1]);
      this.temp.selected = this.selected;
      this.close();
    },

    click() {
      this.isVisible = !this.isVisible;
    },

    close() {
      this.isVisible = false;
    },

    dayclick() {
      this.temp.selected = {};
    },

    drag(range) {
      if (!range) {
        return;
      }
      this.isDragging = true;
      this.temp.dragStartDate = range.start;
    },

    init() {
      this.calendar.maxDate = this.range[1];
      this.selected = {};
      this.temp.dragStartDate = '';
      this.temp.selected = {};
      this.temp.value = {
        start: this.value[0],
        end: this.value[1]
      };
    },

    input() {
      this.isDragging = false;
      this.temp.dragStartDate = '';
    },

    selectListItem(item) {
      let from = new Date(this.range[0]);
      let to = new Date(this.range[1]);

      if (item.unit) {
        from = new Date(to);
        if (item.type === 'comparison') {
          if (item.unit === 'year') {
            from.setFullYear(from.getFullYear() - item.value);
            from.setMonth(from.getMonth() + 1);
            from = utils.getStartDate(from, 'month');
            to = utils.getEndDate(to, 'month');
          }
        } else {
          if (item.unit === 'day') {
            from.setDate(from.getDate() - item.value);
          } else if (item.unit === 'month') {
            from.setMonth(from.getMonth() - item.value);
          } else if (item.unit === 'year') {
            from.setFullYear(from.getFullYear() - item.value);
          }
          from.setDate(from.getDate() + 1);
        }
      }

      this.updateTempValue(from, to);
      this.temp.selected = item;
      this.adjustPage();
    },

    submit() {
      if (this.isDragging) {
        // 日付を1個だけ選択した状態でSubmitした場合は、
        // fromもtoも同じ日付にする
        this.updateTempValue(this.temp.dragStartDate, this.temp.dragStartDate);
      }
      this.selected = this.temp.selected;

      const type = this.selected.type ? this.selected.type : 'default';
      this.close();

      // ウィンドウの横幅が小さくて横スクロールバーが出ている時は
      // modalが消えてからemitしないとグラフの横幅が小さくなるため
      this.$nextTick(() => {
        this.$emit('change', type);
        this.$emit('input', [this.temp.value.start, this.temp.value.end]);
      });
    },

    updateTempValue(from, to) {
      this.temp.value = Object.assign({ start: from, end: to });
    }
  }
};
</script>
